import { inject, Service } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { ConfigService } from '../services/config';
import { SKIP_INTERCEPTOR } from '../services/skip-interceptor.token';
import {
  SigningInvitePageSchema,
  SigningInviteAcceptedSchema,
  type SigningInvitePage,
  type SigningInviteAccepted,
  type SigningInviteRedeem,
} from '../schemas/generated/vantage-api.schemas';

/**
 * The PUBLIC signing-invite API (`/public/v1/signing-invites`, backend #219 / Decision J6) — the
 * web half of the contract `signing_email.py` states outright: the emailed link is
 * `{app}/sign#token=…`, the token rides the URL FRAGMENT (never sent to any server by the
 * browser), and this app reads it client-side and presents it in a POST body.
 *
 * BOTH calls are POST, including the read, deliberately mirroring the backend's own rule: a token
 * in a GET would be a token in the access log, the `Referer`, browser history, and API Gateway's
 * cache. Routes verified against `vantage-backend` `public/signing_invites/routes.py` and the
 * mount in `vantage-infra/config/api_endpoints.py` (CLAUDE.md §19a).
 *
 * There is no auth on these routes — the invite token IS the credential, checked by SECURITY
 * DEFINER functions server-side. `lookup` stamps nothing (a mail client prefetching the link must
 * not burn a single-use token); `redeem` spends the invite and accepts the quote it is BOUND to —
 * the quote id is never in the body, so a token issued for one account cannot be pointed at
 * another account's quote.
 */
@Service()
export class SigningInvitesDataService {
  readonly #http = inject(HttpClient);
  readonly #config = inject(ConfigService);

  get #base(): string {
    return `${this.#config.publicApiBaseUrl}/signing-invites`;
  }

  /**
   * Both calls bypass the global interceptors (`SKIP_INTERCEPTOR`). Two reasons, both load-bearing:
   *
   * 1. The public API's gateway answers the CORS preflight with a STATIC allow-header list
   *    (`Content-Type, X-Amz-Date, X-Api-Key, Cache-Control` — `cdk-infra` `api_rest.py`,
   *    `_PUBLIC_CORS`), so a POST carrying `Idempotency-Key` / `Authorization` / `X-Territory-Id`
   *    is refused by the BROWSER before it is ever sent — the preflight 204 is a refusal, not a
   *    pass. That is exactly how the first onsite signing on QA died (2026-09-10): mint 201,
   *    navigate to /sign, lookup "CORS error", banner "Cannot reach the server". The deployed
   *    gateway cannot be configured per-header for the public API today (the knob exists only for
   *    the authed APIs); the config-driven fix is filed with infra.
   * 2. It is correct anyway: these routes are unauthenticated (the token IS the credential), so a
   *    signed-in STAFF device on the onsite hand-over must not leak its Cognito Bearer token to a
   *    public endpoint, and the backend's IdempotencyMiddleware no-ops without a cognito_sub.
   */
  readonly #publicContext = new HttpContext().set(SKIP_INTERCEPTOR, true);

  /** `POST /lookup` → what the invite points at, so the page can render. 404 SIGNING_INVITE_INVALID
   * for every dead-token case alike (invalid, expired, revoked, already spent) — deliberately one
   * uninformative refusal, so the page must not pretend to know which. */
  lookup(token: string): Observable<SigningInvitePage> {
    return this.#http
      .post<unknown>(`${this.#base}/lookup`, { token }, { context: this.#publicContext })
      .pipe(map((raw) => parseOrWarn(SigningInvitePageSchema, raw, 'signing-invite page') as SigningInvitePage));
  }

  /** `POST /redeem` → spend the invite and accept its quote in one transaction. The body carries
   * the required `reviewed_terms_sha256` echoed from {@link lookup}, so a quote edited mid-review
   * is refused (`QUOTE_REVIEW_CHANGED`) instead of silently signing different terms. */
  redeem(body: SigningInviteRedeem): Observable<SigningInviteAccepted> {
    return this.#http
      .post<unknown>(`${this.#base}/redeem`, body, { context: this.#publicContext })
      .pipe(map((raw) => parseOrWarn(SigningInviteAcceptedSchema, raw, 'signing-invite receipt') as SigningInviteAccepted));
  }
}

function parseOrWarn<T>(schema: { safeParse: (v: unknown) => { success: boolean; data?: T; error?: unknown } }, raw: unknown, label: string): T {
  const res = schema.safeParse(raw);
  if (!res.success) {
    console.warn(`[signing-invites] ${label} drifted from the OpenAPI contract`, (res.error as { issues?: unknown })?.issues);
    return (raw ?? {}) as T;
  }
  return res.data as T;
}
